import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { canAccess, homeFor, useAuth } from './lib/auth.jsx'
import { Spinner } from './components/Ui.jsx'
import Layout from './components/Layout.jsx'
import PublicLayout from './components/PublicLayout.jsx'

import Login from './pages/Login.jsx'
import Directory from './pages/public/Directory.jsx'
import PublicHatchery from './pages/public/HatcheryPage.jsx'
import Account from './pages/Account.jsx'

import AdminOverview from './pages/admin/Overview.jsx'
import AdminHatcheries from './pages/admin/Hatcheries.jsx'
import AdminHatcheryForm from './pages/admin/HatcheryForm.jsx'
import AdminHatcheryOnboard from './pages/admin/HatcheryOnboard.jsx'
import AdminShare from './pages/admin/Share.jsx'
import AdminHatcheryDetail from './pages/admin/HatcheryDetail.jsx'
import AdminUsers from './pages/admin/Users.jsx'
import AdminFarmers from './pages/admin/Farmers.jsx'
import AdminNetwork from './pages/admin/Network.jsx'
import AdminSettings from './pages/admin/Settings.jsx'

import HatcheryDashboard from './pages/hatchery/Dashboard.jsx'
import HatcheryBatchForm from './pages/hatchery/BatchForm.jsx'
import HatcheryBatchDetail from './pages/hatchery/BatchDetail.jsx'
import HatcheryOrders from './pages/hatchery/Orders.jsx'
import HatcheryProfile from './pages/hatchery/Profile.jsx'

import OperatorDesk from './pages/operator/Desk.jsx'
import OperatorMatch from './pages/operator/Match.jsx'
import OperatorOrders from './pages/operator/Orders.jsx'
import OperatorOrderDetail from './pages/operator/OrderDetail.jsx'
import OperatorFlag from './pages/operator/FlagDecision.jsx'
import OperatorTrips from './pages/operator/Trips.jsx'
import OperatorFarmers from './pages/operator/Farmers.jsx'
import OperatorEnquiries from './pages/operator/Enquiries.jsx'

import LiaisonTrips from './pages/liaison/Trips.jsx'
import LiaisonTripDetail from './pages/liaison/TripDetail.jsx'

function Protected ({ roles, children }) {
  const { user, loading, offline, hasToken, refresh } = useAuth()
  const location = useLocation()

  if (loading) return <div className="wrap"><Spinner label="Checking your session…" /></div>

  // Signed in, but the API is unreachable. Keep them where they are rather than
  // throwing away a perfectly good session.
  if (!user && offline && hasToken) {
    return (
      <div className="wrap narrow">
        <div className="card"><div className="body">
          <h2 style={{ marginTop: 0 }}>Cannot reach the server</h2>
          <p className="muted">
            The API at <span className="mono">:4000</span> is not responding. It may be restarting.
            Your sign-in is still valid.
          </p>
          <div className="btnrow">
            <button className="btn primary" onClick={refresh}>Try again</button>
          </div>
        </div></div>
      </div>
    )
  }

  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />
  if (roles && !canAccess(user, roles)) {
    return (
      <div className="wrap narrow">
        <div className="card"><div className="body">
          <h2 style={{ marginTop: 0 }}>Not your screen</h2>
          <p className="muted">Your role ({user.role}) cannot open this page.</p>
          <a className="btn" href={homeFor(user)}>Go to my home screen</a>
        </div></div>
      </div>
    )
  }
  return children
}

function Home () {
  const { user, loading } = useAuth()
  if (loading) return <div className="wrap"><Spinner /></div>
  return <Navigate to={user ? homeFor(user) : '/login'} replace />
}

export default function App () {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      {/* Public directory — no sign-in, safe to send to a farmer over WhatsApp */}
      <Route element={<PublicLayout />}>
        <Route path="/hatcheries" element={<Directory />} />
        <Route path="/hatcheries/:id" element={<PublicHatchery />} />
      </Route>
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<Home />} />
        <Route path="/account" element={<Account />} />

        <Route path="/admin" element={<Protected roles={['admin']}><AdminOverview /></Protected>} />
        <Route path="/admin/hatcheries" element={<Protected roles={['admin']}><AdminHatcheries /></Protected>} />
        <Route path="/admin/hatcheries/new" element={<Protected roles={['admin']}><AdminHatcheryOnboard /></Protected>} />
        <Route path="/admin/hatcheries/:id" element={<Protected roles={['admin']}><AdminHatcheryDetail /></Protected>} />
        <Route path="/admin/hatcheries/:id/edit" element={<Protected roles={['admin']}><AdminHatcheryForm /></Protected>} />
        <Route path="/admin/users" element={<Protected roles={['admin']}><AdminUsers /></Protected>} />
        <Route path="/admin/farmers" element={<Protected roles={['admin']}><AdminFarmers /></Protected>} />
        <Route path="/admin/network" element={<Protected roles={['admin']}><AdminNetwork /></Protected>} />
        <Route path="/admin/settings" element={<Protected roles={['admin']}><AdminSettings /></Protected>} />
        {/* The desk shares this link with farmers, so an operator needs it too. */}
        <Route path="/admin/share" element={<Protected roles={['admin', 'operator']}><AdminShare /></Protected>} />

        <Route path="/hatchery" element={<Protected roles={['hatchery', 'admin']}><HatcheryDashboard /></Protected>} />
        <Route path="/hatchery/batches/new" element={<Protected roles={['hatchery', 'admin']}><HatcheryBatchForm /></Protected>} />
        <Route path="/hatchery/batches/:id" element={<Protected roles={['hatchery', 'admin']}><HatcheryBatchDetail /></Protected>} />
        <Route path="/hatchery/batches/:id/edit" element={<Protected roles={['hatchery', 'admin']}><HatcheryBatchForm /></Protected>} />
        <Route path="/hatchery/orders" element={<Protected roles={['hatchery', 'admin']}><HatcheryOrders /></Protected>} />
        <Route path="/hatchery/profile" element={<Protected roles={['hatchery', 'admin']}><HatcheryProfile /></Protected>} />

        <Route path="/operator" element={<Protected roles={['operator']}><OperatorDesk /></Protected>} />
        <Route path="/operator/match" element={<Protected roles={['operator']}><OperatorMatch /></Protected>} />
        <Route path="/operator/orders" element={<Protected roles={['operator']}><OperatorOrders /></Protected>} />
        <Route path="/operator/orders/:id" element={<Protected roles={['operator']}><OperatorOrderDetail /></Protected>} />
        <Route path="/operator/batches/:id/flag" element={<Protected roles={['operator']}><OperatorFlag /></Protected>} />
        <Route path="/operator/trips" element={<Protected roles={['operator']}><OperatorTrips /></Protected>} />
        <Route path="/operator/farmers" element={<Protected roles={['operator']}><OperatorFarmers /></Protected>} />
        <Route path="/operator/enquiries" element={<Protected roles={['operator']}><OperatorEnquiries /></Protected>} />

        <Route path="/liaison" element={<Protected roles={['liaison']}><LiaisonTrips /></Protected>} />
        <Route path="/liaison/trips/:id" element={<Protected roles={['liaison']}><LiaisonTripDetail /></Protected>} />

        <Route path="*" element={
          <div className="wrap narrow"><div className="card"><div className="body">
            <h2 style={{ marginTop: 0 }}>Page not found</h2>
            <p className="muted">That screen does not exist.</p>
          </div></div></div>
        } />
      </Route>
    </Routes>
  )
}
